import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

/**
 * Reusable hook for server-side searchable Autocomplete components
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.apiEndpoint - API endpoint to fetch from (e.g., '/api/parts')
 * @param {Function} config.buildParams - Function to build query params: (searchTerm) => ({ ... })
 * @param {Function} config.extractData - Function to extract data from response: (response) => [...]
 * @param {string|number} config.selectedValue - Currently selected value (ID)
 * @param {Function} config.getItemEndpoint - Optional: Function to get single item endpoint: (id) => `/api/items/${id}`
 * @param {number} config.debounceMs - Debounce delay in milliseconds (default: 500)
 * @param {number} config.minSearchLength - Minimum characters before searching (default: 2)
 * @param {number} config.pageSize - Number of items per page (default: 50)
 * @param {boolean} config.enabled - When false, skip automatic fetch on mount (default: true)
 * 
 * @returns {Object} - { options, loading, searchInput, handleInputChange, refresh }
 */
export const useServerSideAutocomplete = ({
  apiEndpoint,
  buildParams,
  extractData,
  selectedValue,
  getItemEndpoint,
  debounceMs = 500,
  minSearchLength = 2,
  pageSize = 50,
  enabled = true
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const buildParamsRef = useRef(buildParams);
  const extractDataRef = useRef(extractData);
  const getItemEndpointRef = useRef(getItemEndpoint);

  buildParamsRef.current = buildParams;
  extractDataRef.current = extractData;
  getItemEndpointRef.current = getItemEndpoint;

  // Fetch items with search (optimized with request cancellation)
  const fetchItems = useCallback(async (searchTerm = '') => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    setLoading(true);
    try {
      const params = buildParamsRef.current(searchTerm, pageSize);
      const response = await axios.get(apiEndpoint, { 
        params,
        signal: abortController.signal
      });
      
      // Only update if request wasn't aborted
      if (!abortController.signal.aborted) {
        const items = extractDataRef.current(response);
        setOptions(items);
      }
    } catch (error) {
      // Ignore abort errors (expected when canceling previous requests)
      if (error.name !== 'CanceledError' && error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
        console.error(`Error fetching items from ${apiEndpoint}:`, error);
        setOptions([]);
      }
    } finally {
      // Only update loading state if this is still the current request
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [apiEndpoint, pageSize]);

  // Initialize items on mount (or when enabled becomes true)
  useEffect(() => {
    if (!enabled) return;
    fetchItems('');
  }, [fetchItems, enabled]);

  // Debounced search handler with minimum character threshold
  const handleInputChange = useCallback((event, newInputValue, reason) => {
    setSearchInput(newInputValue);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If user is typing (not selecting), debounce the search
    if (reason === 'input') {
      const trimmedValue = newInputValue.trim();
      
      // Only search if:
      // 1. User has typed at least minSearchLength characters, OR
      // 2. Input is empty (show initial results)
      if (trimmedValue.length === 0 || trimmedValue.length >= minSearchLength) {
        searchTimeoutRef.current = setTimeout(() => {
          fetchItems(trimmedValue);
        }, debounceMs);
      } else if (trimmedValue.length === 1) {
        // For single character, show initial list (no search)
        fetchItems('');
      }
    } else if (reason === 'clear') {
      // Reset to initial list when cleared
      fetchItems('');
    }
  }, [fetchItems, debounceMs, minSearchLength]);

  // Ensure selected item is in options
  useEffect(() => {
    if (!enabled) return;
    const resolveItemEndpoint = getItemEndpointRef.current;
    if (selectedValue && !loading && resolveItemEndpoint) {
      const isItemInOptions = options.some(item => {
        const itemId = item._id?.toString() || item._id;
        const selectedId = selectedValue?.toString() || selectedValue;
        return itemId === selectedId;
      });

      // If selected item is not in options, fetch it
      if (!isItemInOptions) {
        const endpoint = resolveItemEndpoint(selectedValue);
        axios.get(endpoint)
          .then(response => {
            if (response.data) {
              setOptions(prev => {
                // Check if it's already there to avoid duplicates
                const exists = prev.some(item => {
                  const itemId = item._id?.toString() || item._id;
                  const selectedId = selectedValue?.toString() || selectedValue;
                  return itemId === selectedId;
                });
                return exists ? prev : [...prev, response.data];
              });
            }
          })
          .catch(error => {
            console.error('Error fetching selected item:', error);
          });
      }
    }
  }, [selectedValue, options, loading, enabled]);

  // Cleanup timeout and abort controller on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Refresh function to manually reload options
  const refresh = useCallback(() => {
    fetchItems(searchInput.trim());
  }, [fetchItems, searchInput]);

  return {
    options,
    loading,
    searchInput,
    handleInputChange,
    refresh
  };
};
